import { motion } from "motion/react";
import { GraduationCap, Award, Users, BookOpen } from "lucide-react";

const departments = [
  {
    name: "Languages",
    teachers: 8,
    subjects: ["English", "Kiswahili", "French"],
    color: "from-red-500 to-red-600",
  },
  {
    name: "Mathematics",
    teachers: 6,
    subjects: ["Mathematics", "Mental Math"],
    color: "from-blue-600 to-blue-700",
  },
  {
    name: "Sciences",
    teachers: 7,
    subjects: ["Science", "Agriculture", "Home Science"],
    color: "from-blue-800 to-blue-900",
  },
  {
    name: "Humanities",
    teachers: 6,
    subjects: ["Social Studies", "CRE", "History"],
    color: "from-red-600 to-red-700",
  },
  {
    name: "Creative Arts",
    teachers: 5,
    subjects: ["Art & Craft", "Music", "Drama"],
    color: "from-blue-500 to-blue-600",
  },
  {
    name: "Physical Education",
    teachers: 4,
    subjects: ["PE", "Swimming", "Sports"],
    color: "from-red-500 to-red-600",
  },
];

const facultyStats = [
  { icon: Users, value: "30+", label: "Qualified Teachers" },
  { icon: GraduationCap, value: "680", label: "Student Population" },
  { icon: Award, value: "10+", label: "Years Avg. Experience" },
  { icon: BookOpen, value: "722", label: "Students Graduated" },
];

const featuredTeachers = [
  {
    name: "Evans Mogoi",
    subject: "Senior Head Teacher",
    image: "/head-teacher.png",
    qualification: "Bachelor of Education, Mount Kenya University",
    experience: "15+ years",
    description:
      "Evans Otwere Mogoi is the Head Teacher Hilltop Preparatory School. Mogoi has over 10 years teaching and school management experience. He joined Hilltop in 2014 as the senior teacher teaching Kiswahili and Social Studies. In 2015, he was promoted to the Deputy Head Teacher and later was appointed acting Head Teacher, a position he held for over 8 months. Having fulfilled and showcased great strides in school management, he was confirmed as the School Head Teacher in 2016.Previously Mogoi has taught in Nyahururu Elite School (Nyandarua), St. Hannah’s Preparatory school – Karen (Nairobi) and Light Academy – Lavington (Nairobi). Mr. Mogoi holds a Bachelor of Education degree from Mount Kenya University.He enjoys traveling, sports, reading and listening to music when he is not taking care of his students.",
  },
  {
    name: "George Ojwang",
    subject: "Head Teacher",
    image: "/deputy-head-teacher.jpg",
    qualification: "Bachelor of Education, Mount Kenya University",
    experience: "19 years",
    description:
      "Mr.Ojwang is the deputy headteacher, a position he has held for the last 5 years to date. He has nurtured his teaching experience for a period of 15 years (from 2006 to date). He joined Hilltop Preparatory School in 2016 as a deputy headteacher and head of academics, roles he is serving diligently to date. He previously taught in various schools which include Nyahururu Elite School, Kakamega Hill School, Lepic School-Nairobi where he served as a subject teacher, classroom manager, and senior teacher respectively.He’s a holder of Bachelors of Education -specializing in English and literature from Mount Kenya University. His hobbies are sports and reading.",
  },
  {
    name: "Irene Chepngetich",
    subject: "Finance & Accounts",
    image: "/finance-and-administration.png",
    qualification:
      "Bachelor of Commerce - Catholic University of East and Central Africa",
    experience: "10+ years",
    description:
      "Irene Chepngetich is the Finance and Administration Officer at Hilltop Preparatory School. Irene has over 10 years’ experience in Finance and Administration having worked previously as the Chief Accountant of Elgon View Group of colleges in Eldoret for 6 years, where she rose through the rank. Irene is a Certified Public Accountant (CPA) Kenya and she holds a Bachelor of Commerce degree from the Catholic University of East and Central Africa.Her hobbies include reading and traveling. Irene is a firm believer in education and believes that each child is special in his or her own right and deserves undivided attention.",
  },
];

export function Faculty() {
  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6 border border-white/20"
          >
            Our Educators
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl font-bold text-white mb-6"
          >
            Meet Our Faculty
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-white/80 max-w-2xl mx-auto"
          >
            Passionate, qualified, and dedicated educators shaping the minds of
            tomorrow.
          </motion.p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {facultyStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <stat.icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-3xl font-bold text-slate-900 mb-1">
                  {stat.value}
                </div>
                <div className="text-slate-600 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Teachers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-4">
              School Heads
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900">
              Featured Faculty
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center mx-auto max-w-5xl">
            {featuredTeachers.map((teacher, index) => (
              <motion.div
                key={teacher.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 h-full flex flex-col">
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={teacher.image}
                      alt={teacher.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <span className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                        {teacher.subject}
                      </span>
                    </div>
                  </div>

                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="text-lg font-bold text-slate-900 mb-1">
                      {teacher.name}
                    </h3>
                    <p className="text-slate-500 text-sm mb-2">
                      {teacher.qualification}
                    </p>
                    <p className="text-slate-600 text-sm mb-3 flex-1">
                      {teacher.description}
                    </p>
                    <p className="text-red-600 text-sm font-medium">
                      {teacher.experience} experience
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Departments */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Academic Structure
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900">
              Our Departments
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {departments.map((dept, index) => (
              <motion.div
                key={dept.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 h-full">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${dept.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {dept.name}
                  </h3>

                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                    <span>{dept.teachers} Teachers</span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {dept.subjects.map((subject) => (
                      <span
                        key={subject}
                        className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
                      >
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
